import { timeStringToDecimalHours as _timeStringToDecimalHours } from './TimeUtils';

// Re-export interfaces for backward compatibility
export interface BreakTimeOverlap {
  hasOverlap: boolean;
  visibleBreakStart: number;
  visibleBreakEnd: number;
  breakTop: number;
  breakHeight: number;
}

export interface WorkingHoursConfig {
  startTime: string;
  endTime: string;
  breakStartTime?: string;
  breakEndTime?: string;
  workers: number;
}

export interface TimeRange {
  startHour: number;
  endHour: number;
  totalHours: number;
}

/**
 * Converts time string (HH:MM:SS) to minutes since midnight
 */
// const timeStringToMinutes = (timeString: string): number => {
//   const [hours, minutes] = timeString.split(':').map(Number);
//   return hours * 60 + minutes;
// };

/**
 * Converts decimal hours to minutes
 */
// const decimalHoursToMinutes = (decimalHours: number): number => {
//   return Math.round(decimalHours * 60);
// };

/**
 * Converts minutes to decimal hours
 */
// const minutesToDecimalHours = (minutes: number): number => {
//   return minutes / 60;
// };

/**
 * Converts time string to decimal hours
 */
const timeStringToDecimalHours = (timeString: string): number => {
  const [hours, minutes, seconds] = timeString.split(':').map(Number);
  return hours + minutes / 60 + (seconds || 0) / 3600;
};

/**
 * Calculates break time overlap with the visible time range for visualization
 * @param workingHours - Working hours configuration for the date
 * @param timeRange - Overall visible time range
 * @param hourHeight - Height of each hour in pixels
 * @returns Break time overlap information for rendering
 */
export const calculateBreakTimeOverlap = (
  workingHours: WorkingHoursConfig | null,
  timeRange: TimeRange,
  hourHeight: number
): BreakTimeOverlap => {
  // Return no overlap if no working hours or break times
  if (!workingHours || !workingHours.breakStartTime || !workingHours.breakEndTime) {
    return {
      hasOverlap: false,
      visibleBreakStart: 0,
      visibleBreakEnd: 0,
      breakTop: 0,
      breakHeight: 0,
    };
  }

  const breakStartHour = timeStringToDecimalHours(workingHours.breakStartTime);
  const breakEndHour = timeStringToDecimalHours(workingHours.breakEndTime);

  // Calculate break position relative to the visible time range
  // Clamp break times to visible range if they extend beyond it
  const visibleBreakStart = Math.max(breakStartHour, timeRange.startHour);
  const visibleBreakEnd = Math.min(breakEndHour, timeRange.endHour);

  // Only show break if there's any overlap with visible time range
  if (visibleBreakStart >= visibleBreakEnd) {
    return {
      hasOverlap: false,
      visibleBreakStart: 0,
      visibleBreakEnd: 0,
      breakTop: 0,
      breakHeight: 0,
    };
  }

  // Calculate break position and height in pixels
  const breakTop = (visibleBreakStart - timeRange.startHour) * hourHeight;
  const breakHeight = (visibleBreakEnd - visibleBreakStart) * hourHeight;

  return {
    hasOverlap: true,
    visibleBreakStart,
    visibleBreakEnd,
    breakTop,
    breakHeight,
  };
};

/**
 * Checks if a task time overlaps with break time
 * @param taskStartHour - Task start time in decimal hours
 * @param taskDurationHours - Task duration in hours
 * @param workingHours - Working hours configuration
 * @returns True if the task overlaps with break time
 */
export const isTaskOverlappingBreak = (
  taskStartHour: number,
  taskDurationHours: number,
  workingHours: WorkingHoursConfig | null
): boolean => {
  if (!workingHours || !workingHours.breakStartTime || !workingHours.breakEndTime) {
    return false;
  }

  const breakStartHour = timeStringToDecimalHours(workingHours.breakStartTime);
  const breakEndHour = timeStringToDecimalHours(workingHours.breakEndTime);
  const taskEndHour = taskStartHour + taskDurationHours;

  // Check if task overlaps with break time
  return (
    (taskStartHour < breakEndHour && taskEndHour > breakStartHour) ||
    (taskStartHour >= breakStartHour && taskStartHour < breakEndHour) ||
    (taskEndHour > breakStartHour && taskEndHour <= breakEndHour)
  );
};

/**
 * Calculates the duration of break overlap for a task
 * @param taskStartHour - Task start time in decimal hours
 * @param taskDurationHours - Task duration in hours
 * @param workingHours - Working hours configuration
 * @returns Duration of overlap in hours
 */
export const calculateBreakOverlapDuration = (
  taskStartHour: number,
  taskDurationHours: number,
  workingHours: WorkingHoursConfig | null
): number => {
  if (!workingHours || !workingHours.breakStartTime || !workingHours.breakEndTime) {
    return 0;
  }

  const breakStartHour = timeStringToDecimalHours(workingHours.breakStartTime);
  const breakEndHour = timeStringToDecimalHours(workingHours.breakEndTime);
  const taskEndHour = taskStartHour + taskDurationHours;

  // Calculate overlap duration
  const overlapStart = Math.max(taskStartHour, breakStartHour);
  const overlapEnd = Math.min(taskEndHour, breakEndHour);

  return Math.max(0, overlapEnd - overlapStart);
};

/**
 * Gets the break time range for a given working hours configuration
 * @param workingHours - Working hours configuration
 * @returns Break time range in decimal hours, or null if no break
 */
export const getBreakTimeRange = (
  workingHours: WorkingHoursConfig | null
): { startHour: number; endHour: number } | null => {
  if (!workingHours || !workingHours.breakStartTime || !workingHours.breakEndTime) {
    return null;
  }

  return {
    startHour: timeStringToDecimalHours(workingHours.breakStartTime),
    endHour: timeStringToDecimalHours(workingHours.breakEndTime),
  };
};

/**
 * Checks if a time falls within break hours
 * @param timeHour - Time in decimal hours
 * @param workingHours - Working hours configuration
 * @returns True if the time is during break hours
 */
export const isBreakTime = (timeHour: number, workingHours: WorkingHoursConfig | null): boolean => {
  const breakRange = getBreakTimeRange(workingHours);
  if (!breakRange) {
    return false;
  }

  return timeHour >= breakRange.startHour && timeHour < breakRange.endHour;
};

/**
 * Calculates available working time excluding breaks
 * @param startHour - Start time in decimal hours
 * @param endHour - End time in decimal hours
 * @param workingHours - Working hours configuration
 * @returns Available working time in hours
 */
export const calculateAvailableWorkingTime = (
  startHour: number,
  endHour: number,
  workingHours: WorkingHoursConfig | null
): number => {
  const breakRange = getBreakTimeRange(workingHours);
  if (!breakRange) {
    return endHour - startHour;
  }

  // Calculate time before break
  const timeBeforeBreak = Math.max(0, Math.min(breakRange.startHour, endHour) - startHour);

  // Calculate time after break
  const timeAfterBreak = Math.max(0, endHour - Math.max(breakRange.endHour, startHour));

  return timeBeforeBreak + timeAfterBreak;
};
